<template id="authenticated-pdf-viewer">
  <canvas :id="id" :class="cssClass" :style="style" ref="viewerCanvas" :draggable="draggable"></canvas>
</template>

<script>
import { defineComponent, computed, onBeforeUnmount, watch, ref, shallowRef } from 'vue';

import { PDFReader } from '@/utils/pdfjs';

import { useStore } from 'vuex';

import _ from 'lodash';

export default defineComponent({
  name: 'AuthenticatedPDFViewer',
  props: {
    'id': String,
    'cssClass': String,
    'style': String,
    'type': String,
    'mimeType': String,
    'draggable': {
      type: Boolean,
      default: false
    },
    'mediaUrl': String,
    'currentPage': {
      type: Number,
      default: 1
    },
    'zoomValue': {
      type: Number,
      default: 1
    }
  },
  emits: [
    'update:mediaCapabilities',
    'update:currentPage'
  ],
  setup(props, { emit }) {
    //TODO Later use emit to show when password is needed and get password from user
    const store = useStore();

    const viewerCanvas = ref(null);

    const loadedDocument = shallowRef(null);

    const objectURL = ref(undefined);

    //Save fallback separately to not accidentally revoke an outside object URL
    const fallbackURL = ref(undefined);

    const computedURL = computed(() => {
      if (objectURL.value != null && objectURL.value.url != null) return objectURL.value.url;
      if (fallbackURL.value != null) return fallbackURL.value;
      return null;
    });

    watch(() => props.currentPage, (newPage) => {
      if (loadedDocument.value != null) {
        let updatedPage = loadedDocument.value.setPage(newPage);
        if (newPage != updatedPage) emit('update:currentPage', updatedPage); //Emit page if it got adjusted by the viewer from the requested one
      }
    });

    watch(viewerCanvas, (newCanvas) => {
      if (loadedDocument.value != null) {
        loadedDocument.value.setCanvas(newCanvas);
      }
    });

    //DISABLED dynamic zoom rerendering for now because of visual issues with version 3.11.174 -> Set below slightly higher instead, statically on load 
    /* watch(() => props.zoomValue, (newZoomValue, oldZoomValue) => {
      if (oldZoomValue != newZoomValue) {
        loadedDocument.value.setScales({renderingScale: newZoomValue});
      }
    }); */

    //On any change load the new document
    watch(computedURL, (newURL, oldURL) => {
      if (newURL != null && !(_.isEqual(newURL, oldURL))) {
        let reader = new PDFReader(viewerCanvas.value, {renderingScale: 2.0});
        return reader.loadPDF(newURL)
          .then(() => {
            loadedDocument.value = reader;
            emit('update:mediaCapabilities', {
              hasPages: true,
              pages: reader.pageCount
            });
          })
          .catch((error) => {
            console.error('Could not load PDF document for viewer', error);
            loadedDocument.value = null;
          });
      }
    }, { immediate: true });

    const loadedThroughRelay = computed(() => store.getters['auth/isAuthenticationRelayActive']);

    /* const crossoriginAttribute = computed(() => {
      //If loaded over the network, set to anonymous
      if (loadedThroughRelay.value) {
        return 'anonymous';
      }
      return undefined;
    }); */

    watch([() => props.mediaUrl, loadedThroughRelay], ([url, throughRelay]) => {
      const oldURL = objectURL.value;
      
      //Anonymous async function to retrieve a promise for laoding to either complete or fail or never be triggered
      let loadPromise = (async () => {
        if (!(throughRelay))  { //Load it manually - no relay available
          await store.dispatch('fetchMediaWithAuthentication', {mediaURL: url})
          .then((fetchResult) => {
            objectURL.value = fetchResult;
          })
          .catch((error) => {
            console.error('Error loading media with authentication', error);
            objectURL.value = undefined;
          });
        }
      })();
      //Dispatch revoke to not trigger an error by revoking while it is still used
      setTimeout(() => {
        if (oldURL != null && oldURL.needsRevoking) URL.revokeObjectURL(oldURL.url);
      }, 1000);

      //If either the relay is responsible or local files are accessed or any other edge case, set the URL and let the browser handle the loading
      //Set after loading to not immediately show a faulty media
      loadPromise.finally(() => fallbackURL.value = url);
    }, { immediate: true });

    onBeforeUnmount(() => {
      const oldURL = objectURL.value;
      objectURL.value = undefined;
      //Dispatch revoke to not trigger image error by revoking while it is still set as the src
      setTimeout(() => {
        if (oldURL != null && oldURL.needsRevoking) URL.revokeObjectURL(oldURL.url);
      }, 1000); 
    });

    return { computedURL, viewerCanvas }
  },
})
</script>

<style scoped>
</style>